import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Shield, KeyRound, Smartphone, FileSearch, RefreshCw, CheckCircle2, XCircle, AlertTriangle, HardDrive, Download, Play, Mail } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ["master", "admin", "workorder", "store", "spd", "accounts", "hr"];

const AdminSecurity = () => {
  // Change password state
  const [pwdRole, setPwdRole] = useState("admin");
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);

  // 2FA state
  const [twoFaEnabled, setTwoFaEnabled] = useState([]);
  const [twoFaLoading, setTwoFaLoading] = useState(false);

  // Audit log state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [userFilter, setUserFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");

  // Backup state
  const [backupLogs, setBackupLogs] = useState([]);
  const [backupFiles, setBackupFiles] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);

  useEffect(() => {
    // 2FA and audit log tabs are hidden (Feb 2026) so skip those fetches —
    // saves two network round-trips on every Security Center page load.
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    setBackupLoading(true);
    try {
      const r = await axios.get(`${API}/backup/history?limit=20`);
      setBackupLogs(r.data.logs || []);
      setBackupFiles(r.data.local_files || []);
    } catch (e) { console.error(e); }
    finally { setBackupLoading(false); }
  };

  const runBackupNow = async () => {
    setBackupRunning(true);
    try {
      await axios.post(`${API}/backup/run-now`);
      toast.success("Backup created successfully");
      fetchBackups();
    } catch (e) { toast.error("Backup failed"); }
    finally { setBackupRunning(false); }
  };

  const downloadLatest = async () => {
    try {
      const response = await axios.post(`${API}/backup/download-latest`, {}, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '').substring(0, 15);
      a.href = url;
      a.download = `cpi_backup_${timestamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded. Save it somewhere safe!");
      fetchBackups();
    } catch (e) { toast.error("Download failed"); }
  };

  const downloadFile = async (filename) => {
    try {
      const response = await axios.get(`${API}/backup/download/${filename}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const fetch2FA = async () => {
    try {
      const r = await axios.get(`${API}/auth/2fa/status`);
      setTwoFaEnabled(r.data.enabled_roles || []);
    } catch (e) { console.error(e); }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({ limit: 300 });
      if (userFilter) params.set("user", userFilter);
      if (methodFilter !== "all") params.set("method", methodFilter);
      const r = await axios.get(`${API}/audit/logs?${params}`);
      setLogs(r.data.logs || []);
    } catch (e) { console.error(e); }
    finally { setLogsLoading(false); }
  };

  const handleChangePwd = async () => {
    if (!newPwd || newPwd.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("Passwords don't match"); return; }
    setPwdLoading(true);
    try {
      // Master can omit current_password — backend accepts empty for master sessions.
      await axios.post(`${API}/auth/change-password`, {
        role: pwdRole, current_password: currentPwd || "", new_password: newPwd,
      });
      toast.success(`Password changed for ${pwdRole}`);
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally { setPwdLoading(false); }
  };

  const toggle2FA = async (role) => {
    const currentlyEnabled = twoFaEnabled.includes(role);
    setTwoFaLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/toggle`, { role, enabled: !currentlyEnabled });
      toast.success(`2FA ${!currentlyEnabled ? "enabled" : "disabled"} for ${role}`);
      fetch2FA();
    } catch (e) {
      toast.error("Failed to toggle 2FA");
    } finally { setTwoFaLoading(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-IN") : "—";
  const methodColor = (m) => ({
    POST: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    PUT: "bg-blue-500/20 text-blue-300 border-blue-500/40",
    PATCH: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
    DELETE: "bg-red-500/20 text-red-300 border-red-500/40",
  }[m] || "bg-gray-500/20 text-gray-300 border-gray-500/40");
  const statusColor = (s) => s >= 500 ? "text-red-400" : s >= 400 ? "text-amber-400" : "text-emerald-400";

  // ---- Audit log readability helpers ----
  // Map first role token to a friendly badge. `path` is used to special-case
  // anonymous system-level events (subscription state changes, scheduled
  // backups, etc.) so they show as "System" instead of "Anonymous" — those
  // entries are produced by the backend on the customer's behalf.
  const roleBadge = (userStr, path) => {
    const isSystemPath = path && (
      path.startsWith("/api/subscription/") ||
      path.startsWith("/api/backup/")
    );
    if (!userStr || userStr === "anonymous") {
      if (isSystemPath) {
        return { label: "System", color: "bg-violet-500/20 text-violet-300 border-violet-500/50" };
      }
      return { label: "Anonymous", color: "bg-gray-700 text-gray-300 border-gray-600" };
    }
    const primary = userStr.split(",")[0];
    const map = {
      master:    { label: "Vendor",    color: "bg-orange-500/20 text-orange-300 border-orange-500/50" },
      admin:     { label: "Admin",     color: "bg-cyan-500/20 text-cyan-300 border-cyan-500/50" },
      hr:        { label: "HR",        color: "bg-teal-500/20 text-teal-300 border-teal-500/50" },
      workorder: { label: "Work Order", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/50" },
      store:     { label: "Store",     color: "bg-amber-500/20 text-amber-300 border-amber-500/50" },
      spd:       { label: "SPD",       color: "bg-blue-500/20 text-blue-300 border-blue-500/50" },
      accounts:  { label: "Accounts",  color: "bg-indigo-500/20 text-indigo-300 border-indigo-500/50" },
    };
    return map[primary] || { label: primary, color: "bg-gray-500/20 text-gray-300 border-gray-500/40" };
  };

  // Translate path + method into a plain-English action that a non-technical
  // user can understand at a glance. We deliberately drop UUIDs and other
  // noise, and use natural verbs ("Added", "Edited", "Deleted") instead of
  // HTTP terms.
  const describeAction = (method, path) => {
    if (!path) return "—";
    const p = path.replace(/^\/api\//, "");
    const verbMap = {
      POST: "Added new",
      PUT: "Edited",
      PATCH: "Edited",
      DELETE: "Deleted",
    };
    const verb = verbMap[method] || method;

    const segs = p.split("?")[0].split("/").filter(Boolean);
    const head = segs[0] || "";

    // Friendly resource names — singular for write operations.
    const labels = {
      "work-orders": "a work order",
      "work-orders-new": "a work order",
      "wo-customers": "a customer",
      "customers": "a customer",
      "vendors": "a vendor",
      "employees": "an employee",
      "salary-configs": "a salary configuration",
      "payroll": "a payroll record",
      "attendance": "an attendance entry",
      "advances": "an advance payment",
      "increments": "a salary increment",
      "inventory": "an inventory item",
      "quotations": "a quotation",
      "vouchers": "a voucher",
      "voucher-types": "a voucher type",
      "tax-rates": "a tax rate",
      "invoice-settings": "invoice settings",
      "delivery-challans": "a delivery challan",
      "spd-orders": "an SPD order",
      "machines": "a machine",
      "machine-maintenance": "a maintenance record",
      "product-specifications": "a product specification",
      "materials": "a material",
      "purchase-orders": "a purchase order",
      "dies": "a die",
      "labels": "a label",
      "timesheet": "a timesheet entry",
      "completion-history": "a completion record",
    };

    // ---- Special, more readable cases ----
    if (head === "auth") {
      if (segs[1] === "validate" && method === "POST") return "Logged in";
      if (segs[1] === "logout") return "Logged out";
      if (segs[1] === "change-password") return "Changed password";
      if (segs[1] === "2fa" && segs[2] === "toggle") return "Updated 2FA setting";
      if (segs[1] === "2fa" && segs[2] === "verify") return "Verified 2FA code";
      if (segs[1] === "forgot-password") return "Requested password reset";
      if (segs[1] === "reset-password") return "Reset password";
      return "Account activity";
    }
    if (head === "backup") {
      if (segs[1] === "run-now") return "Ran a database backup";
      if (segs[1] === "download-latest") return "Downloaded latest backup";
      if (segs[1] === "download") return "Downloaded a backup file";
      return "Backup operation";
    }
    if (head === "subscription") {
      if (segs[1] === "mark-paid") return "Recorded a monthly payment";
      if (segs[1] === "lock") return "Locked the app";
      if (segs[1] === "unlock") return "Unlocked the app";
      if (segs[1] === "change-plan") return "Changed the subscription plan";
      if (segs[1] === "update-pricing") return "Updated subscription pricing";
      return "Subscription change";
    }
    if (head === "hr" && segs[1]) {
      const friendly = labels[segs[1]] || segs[1].replace(/-/g, " ");
      // /hr/payroll/generate is a special compound action
      if (segs[1] === "payroll" && segs[2] === "generate") return "Generated monthly payroll";
      if (segs[1] === "attendance" && segs[2] === "sync-from-timesheet") return "Synced attendance from timesheet";
      if (segs[1] === "attendance" && segs[2] === "bulk-upload") return "Uploaded bulk attendance";
      if (segs[1] === "attendance" && segs[2] === "upload") return "Uploaded an attendance file";
      return `${verb} ${friendly}`;
    }
    if (head === "accounts" && segs[1]) {
      if (segs[1] === "vouchers" && segs[2] === "export-tally-xml") return "Exported vouchers to Tally";
      const friendly = labels[segs[1]] || segs[1].replace(/-/g, " ");
      return `${verb} ${friendly}`;
    }
    if (head === "store" && segs[1] === "stock-out") return `${verb === "Added new" ? "Issued" : verb} stock`;

    const friendly = labels[head] || head.replace(/-/g, " ");
    return `${verb} ${friendly}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6" data-testid="admin-security-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-amber-400" />
          <div>
            <h1 className="text-3xl font-bold text-white">Security Center</h1>
            <p className="text-gray-400 text-sm">Passwords · Backups</p>
          </div>
        </div>

        <Tabs defaultValue="passwords">
          <TabsList className="bg-gray-900 border border-gray-800">
            <TabsTrigger value="passwords" className="data-[state=active]:bg-amber-600" data-testid="tab-passwords"><KeyRound className="h-4 w-4 mr-1.5" /> Passwords</TabsTrigger>
            {/* Two-Factor and Audit Log tabs removed from UI (Feb 2026, both
                packages). Backend endpoints, DB collections, and write hooks
                remain intact — change is purely cosmetic and easily reversible. */}
            <TabsTrigger value="backup" className="data-[state=active]:bg-amber-600" data-testid="tab-backup"><HardDrive className="h-4 w-4 mr-1.5" /> Backups</TabsTrigger>
          </TabsList>

          {/* PASSWORDS */}
          <TabsContent value="passwords" className="mt-4">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-amber-300 mb-1">Change role password</h3>
                  <p className="text-xs text-gray-400">Override the default .env password for any role. As <b className="text-orange-300">master</b>, you may leave the current password blank — you can reset any role's password directly.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Role</Label>
                    <Select value={pwdRole} onValueChange={setPwdRole}>
                      <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="select-role"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-600">
                        {ROLES.map(r => <SelectItem key={r} value={r} className="text-white capitalize">{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Current password <span className="text-xs text-gray-500">(optional for master)</span></Label>
                    <Input type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} placeholder="Leave blank to override as master" className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="input-current-pwd" />
                  </div>
                  <div>
                    <Label className="text-gray-300">New password <span className="text-xs text-gray-500">(min 8 chars)</span></Label>
                    <Input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="input-new-pwd" />
                  </div>
                  <div>
                    <Label className="text-gray-300">Confirm new password</Label>
                    <Input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="input-confirm-pwd" />
                  </div>
                </div>
                <Button onClick={handleChangePwd} disabled={pwdLoading} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-change-pwd">
                  {pwdLoading ? "Changing..." : "Change Password"}
                </Button>
                <div className="bg-amber-500/10 border-l-4 border-amber-500 p-3 rounded text-xs text-amber-200">
                  <AlertTriangle className="h-4 w-4 inline mr-1" /> <b>Recommendation:</b> Change the default password for every role right after installing. Defaults are widely known.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* 2FA */}
          <TabsContent value="2fa" className="mt-4">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-amber-300 mb-1">Two-Factor Authentication (Email OTP)</h3>
                  <p className="text-xs text-gray-400">When enabled, logging into that role requires a 6-digit code emailed to the role's email address (configured via <code className="bg-gray-800 px-1 rounded">ADMIN_EMAIL</code>, <code className="bg-gray-800 px-1 rounded">HR_EMAIL</code>, etc. env vars).</p>
                </div>
                <div className="divide-y divide-gray-800">
                  {ROLES.map(role => {
                    const enabled = twoFaEnabled.includes(role);
                    return (
                      <div key={role} className="flex items-center justify-between py-3" data-testid={`2fa-row-${role}`}>
                        <div className="flex items-center gap-3">
                          <span className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center uppercase text-xs text-gray-300 font-bold">{role[0]}</span>
                          <div>
                            <div className="text-gray-200 font-medium capitalize">{role}</div>
                            <div className="text-xs text-gray-500">{enabled ? "Login requires a 6-digit OTP" : "Password-only login"}</div>
                          </div>
                        </div>
                        <Button onClick={() => toggle2FA(role)} disabled={twoFaLoading} variant="outline" className={enabled ? "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10" : "border-gray-700 text-gray-400 hover:bg-gray-800"}>
                          {enabled ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Enabled</> : <><XCircle className="h-4 w-4 mr-1" /> Disabled</>}
                        </Button>
                      </div>
                    );
                  })}
                </div>
                <div className="bg-blue-500/10 border-l-4 border-blue-500 p-3 rounded text-xs text-blue-200">
                  <AlertTriangle className="h-4 w-4 inline mr-1" /> <b>Setup needed:</b> For OTPs to actually send, configure SMTP env vars: <code className="bg-gray-800 px-1 rounded">SMTP_HOST</code>, <code className="bg-gray-800 px-1 rounded">SMTP_USERNAME</code>, <code className="bg-gray-800 px-1 rounded">SMTP_PASSWORD</code>, and <code className="bg-gray-800 px-1 rounded">ADMIN_EMAIL</code> (or per-role emails). Until then, the OTP is shown in the login response for testing.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AUDIT */}
          <TabsContent value="audit" className="mt-4">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-amber-300 mb-1">Audit log</h3>
                    <p className="text-xs text-gray-400">Every create / update / delete operation recorded with user, timestamp, IP and result.</p>
                  </div>
                  <Button onClick={fetchLogs} disabled={logsLoading} variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10" data-testid="btn-refresh-logs">
                    <RefreshCw className={`h-4 w-4 mr-1 ${logsLoading ? "animate-spin" : ""}`} /> Refresh
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input placeholder="Filter by user..." value={userFilter} onChange={e => setUserFilter(e.target.value)} className="bg-gray-800 border-gray-600 text-white" data-testid="filter-user" />
                  <Select value={methodFilter} onValueChange={setMethodFilter}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="filter-method"><SelectValue placeholder="Method" /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">
                      <SelectItem value="all" className="text-white">All Methods</SelectItem>
                      <SelectItem value="POST" className="text-white">POST (Create)</SelectItem>
                      <SelectItem value="PUT" className="text-white">PUT (Update)</SelectItem>
                      <SelectItem value="PATCH" className="text-white">PATCH (Partial)</SelectItem>
                      <SelectItem value="DELETE" className="text-white">DELETE</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={fetchLogs} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-apply-filter">Apply Filter</Button>
                </div>
                <div className="bg-gray-900/60 rounded border border-gray-800 overflow-hidden max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-gray-900">
                      <TableRow className="border-gray-800 hover:bg-transparent">
                        <TableHead className="text-gray-400 text-xs">When</TableHead>
                        <TableHead className="text-gray-400 text-xs">Who</TableHead>
                        <TableHead className="text-gray-400 text-xs">What</TableHead>
                        <TableHead className="text-gray-400 text-xs">IP</TableHead>
                        <TableHead className="text-gray-400 text-xs text-right">Result</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">No audit entries yet. Any change made in the app will appear here.</TableCell></TableRow>
                      ) : logs.map((l, i) => {
                        const who = roleBadge(l.user, l.path);
                        const action = describeAction(l.method, l.path);
                        const okStatus = l.status >= 200 && l.status < 400;
                        return (
                          <TableRow key={i} className="border-gray-800 hover:bg-gray-800/30" data-testid={`audit-row-${i}`}>
                            <TableCell className="text-xs text-gray-300 font-mono whitespace-nowrap">{fmtDate(l.timestamp)}</TableCell>
                            <TableCell><Badge variant="outline" className={`text-[10px] ${who.color}`}>{who.label}</Badge></TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-200">{action}</span>
                              <div className="text-[10px] text-gray-600 font-mono mt-0.5 truncate" title={l.path}>{l.method} {l.path}</div>
                            </TableCell>
                            <TableCell className="text-xs text-gray-400 font-mono">{l.ip || "—"}</TableCell>
                            <TableCell className={`text-xs text-right font-bold ${statusColor(l.status)}`}>
                              {okStatus ? `Success` : `Failed (${l.status})`}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-gray-500">Showing {logs.length} latest records. Logs are stored permanently in MongoDB (<code className="bg-gray-800 px-1 rounded">audit_logs</code> collection).</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* BACKUPS */}
          <TabsContent value="backup" className="mt-4">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-6 space-y-5">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-amber-300 mb-1">Database Backups</h3>
                    <p className="text-xs text-gray-400">Daily automatic backup runs at <b>2:00 AM IST</b>. You can also download a fresh backup anytime.</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={fetchBackups} disabled={backupLoading} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800" data-testid="btn-refresh-backups">
                      <RefreshCw className={`h-4 w-4 mr-1 ${backupLoading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                    <Button onClick={runBackupNow} disabled={backupRunning} variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10" data-testid="btn-run-backup">
                      <Play className={`h-4 w-4 mr-1 ${backupRunning ? "animate-pulse" : ""}`} /> {backupRunning ? "Running..." : "Run Backup Now"}
                    </Button>
                    <Button onClick={downloadLatest} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-download-latest">
                      <Download className="h-4 w-4 mr-1" /> Download Fresh Backup
                    </Button>
                  </div>
                </div>

                <div className="bg-emerald-500/10 border-l-4 border-emerald-500 p-3 rounded text-xs text-emerald-200">
                  <CheckCircle2 className="h-4 w-4 inline mr-1" /> <b>Protect your data:</b> Click <b>Download Fresh Backup</b> weekly. Save the JSON file to your email drafts, Dropbox, or Google Drive. If anything ever happens, we can restore the entire database from one of these files in minutes.
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                    <HardDrive className="h-4 w-4" /> Available Downloads <span className="text-xs font-normal text-gray-500">({backupFiles.length} files, {backupFiles.reduce((s, f) => s + f.size_kb, 0).toFixed(0)} KB total)</span>
                  </h4>
                  <div className="bg-gray-900/60 rounded border border-gray-800 overflow-hidden max-h-72 overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-gray-900">
                        <TableRow className="border-gray-800 hover:bg-transparent">
                          <TableHead className="text-gray-400 text-xs">File</TableHead>
                          <TableHead className="text-gray-400 text-xs">Size</TableHead>
                          <TableHead className="text-gray-400 text-xs">Created</TableHead>
                          <TableHead className="text-gray-400 text-xs text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {backupFiles.length === 0 ? (
                          <TableRow><TableCell colSpan={4} className="text-center text-gray-500 py-6">No local backups yet. Click "Run Backup Now" to create one.</TableCell></TableRow>
                        ) : backupFiles.map((f, i) => (
                          <TableRow key={i} className="border-gray-800 hover:bg-gray-800/30">
                            <TableCell className="text-xs text-cyan-300 font-mono">{f.filename}</TableCell>
                            <TableCell className="text-xs text-gray-300 font-mono">{f.size_kb} KB</TableCell>
                            <TableCell className="text-xs text-gray-400 font-mono">{fmtDate(f.created_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button onClick={() => downloadFile(f.filename)} size="sm" variant="ghost" className="text-amber-300 hover:bg-amber-500/10 h-7">
                                <Download className="h-3 w-3 mr-1" /> Download
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Email Delivery History
                  </h4>
                  <div className="bg-gray-900/60 rounded border border-gray-800 overflow-hidden max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-gray-900">
                        <TableRow className="border-gray-800 hover:bg-transparent">
                          <TableHead className="text-gray-400 text-xs">Time</TableHead>
                          <TableHead className="text-gray-400 text-xs">File</TableHead>
                          <TableHead className="text-gray-400 text-xs">Size</TableHead>
                          <TableHead className="text-gray-400 text-xs">Emailed?</TableHead>
                          <TableHead className="text-gray-400 text-xs">Note</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {backupLogs.length === 0 ? (
                          <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-6">No backup history yet.</TableCell></TableRow>
                        ) : backupLogs.map((l, i) => (
                          <TableRow key={i} className="border-gray-800 hover:bg-gray-800/30">
                            <TableCell className="text-xs text-gray-300 font-mono">{fmtDate(l.timestamp)}</TableCell>
                            <TableCell className="text-xs text-cyan-300 font-mono truncate max-w-[200px]">{l.file}</TableCell>
                            <TableCell className="text-xs text-gray-300 font-mono">{l.size_kb} KB</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-[10px] ${l.emailed ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-gray-500/20 text-gray-300 border-gray-500/40"}`}>
                                {l.emailed ? <><CheckCircle2 className="h-3 w-3 mr-1" /> Sent</> : <><XCircle className="h-3 w-3 mr-1" /> Local only</>}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-gray-400 max-w-[250px] truncate" title={l.error || l.emailed_to}>{l.emailed_to || l.error || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {backupLogs.some(l => l.emailed) ? (
                  <div className="bg-green-500/10 border-l-4 border-green-500 p-3 rounded text-xs text-green-200" data-testid="email-backup-enabled-banner">
                    <CheckCircle2 className="h-4 w-4 inline mr-1" /> <b>Email backups are active.</b> Each nightly backup is also emailed as an attachment to your configured recipient. You can keep email as a second off-site copy — just never delete those messages.
                  </div>
                ) : (
                  <div className="bg-blue-500/10 border-l-4 border-blue-500 p-3 rounded text-xs text-blue-200" data-testid="email-backup-setup-banner">
                    <AlertTriangle className="h-4 w-4 inline mr-1" /> <b>To enable automatic email backups</b>, add these to <code className="bg-gray-800 px-1 rounded">/app/backend/.env</code>: <code className="bg-gray-800 px-1 rounded">SMTP_HOST</code>, <code className="bg-gray-800 px-1 rounded">SMTP_USER</code>, <code className="bg-gray-800 px-1 rounded">SMTP_PASSWORD</code>, <code className="bg-gray-800 px-1 rounded">BACKUP_EMAIL_TO</code>, and <code className="bg-gray-800 px-1 rounded">BACKUP_EMAIL_ENABLED=true</code>. For Gmail, use an app-specific password.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminSecurity;
